import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { Container, SectionHeading } from "./common";
import { TESTIMONIALS } from "../lib/content";
import { fetchTestimonials } from "../lib/api";
import { useApiData } from "../hooks/useApiData";

const AUTOPLAY_MS = 5000;

const mapTestimonialRows = (rows) =>
  rows.map((row) => ({
    id: row.id,
    name: row.name,
    role: row.role,
    photo: row.photo_path,
    text: row.message,
  }));

export const Testimonials = () => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const testimonials = useApiData(fetchTestimonials, TESTIMONIALS, mapTestimonialRows);
  const total = testimonials.length;

  useEffect(() => {
    if (paused) return;
    const timer = setInterval(() => setIndex((i) => (i + 1) % total), AUTOPLAY_MS);
    return () => clearInterval(timer);
  }, [paused, total]);

  const go = (dir) => setIndex((i) => (i + dir + total) % total);
  const active = testimonials[index % total];

  return (
    <section className="py-20 lg:py-28 bg-white" data-testid="testimonials-section">
      <Container>
        <SectionHeading
          overline="In Their Words"
          title="Voices from the communities we serve"
          subtitle="Real stories from the children, women and families whose lives have changed."
          center
        />
        <div
          className="relative mx-auto max-w-3xl"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          data-testid="testimonials-carousel"
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={active.id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="rounded-3xl bg-offwhite p-8 sm:p-12 shadow-soft border border-slate-100 text-center hover:shadow-card hover:-translate-y-1 transition-all"
              data-testid={`testimonial-${active.id}`}
            >
              <Quote className="h-9 w-9 text-coral mx-auto" />
              <p className="mt-6 font-serif italic text-xl sm:text-2xl text-navy leading-relaxed text-balance">
                “{active.text}”
              </p>
              <div className="mt-8 flex flex-col items-center gap-3">
                <img
                  src={active.photo}
                  alt={active.name}
                  className="h-16 w-16 rounded-full object-cover ring-4 ring-teal-50"
                />
                <div>
                  <div className="font-display font-bold text-navy">{active.name}</div>
                  <div className="text-sm text-slate-500">{active.role}</div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          <button
            onClick={() => go(-1)}
            aria-label="Previous testimonial"
            data-testid="testimonial-prev"
            className="hidden sm:grid absolute top-1/2 -left-5 lg:-left-14 -translate-y-1/2 place-items-center h-11 w-11 rounded-full bg-white shadow-soft border border-slate-100 text-teal-700 hover:bg-teal-700 hover:text-white transition-colors"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            onClick={() => go(1)}
            aria-label="Next testimonial"
            data-testid="testimonial-next"
            className="hidden sm:grid absolute top-1/2 -right-5 lg:-right-14 -translate-y-1/2 place-items-center h-11 w-11 rounded-full bg-white shadow-soft border border-slate-100 text-teal-700 hover:bg-teal-700 hover:text-white transition-colors"
          >
            <ChevronRight className="h-5 w-5" />
          </button>

          <div className="mt-8 flex items-center justify-center gap-2">
            {testimonials.map((item, i) => (
              <button
                key={item.id}
                onClick={() => setIndex(i)}
                aria-label={`Go to testimonial ${i + 1}`}
                data-testid={`testimonial-dot-${i}`}
                className={`h-2.5 rounded-full transition-all ${
                  i === index ? "w-8 bg-coral" : "w-2.5 bg-teal-200 hover:bg-teal-300"
                }`}
              />
            ))}
          </div>
        </div>
      </Container>
    </section>
  );
};
